import { createContext, useContext, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "./api";
import { queryKeys } from "./queryKeys";

export type Capabilities = { attachments: boolean; email: boolean };

// Pessimistic defaults: the primary use case is "single-click install with
// nothing configured", where R2 and email are OFF. Showing affordances
// optimistically then yanking them away ~100ms later is more jarring than
// briefly hiding them and revealing once /api/capabilities confirms they
// work.
const DEFAULT_CAPABILITIES: Capabilities = { attachments: false, email: false };

const Ctx = createContext<Capabilities>(DEFAULT_CAPABILITIES);

export function CapabilitiesProvider({ children }: { children: ReactNode }) {
  const { data } = useQuery({
    queryKey: queryKeys.capabilities(),
    queryFn: api.capabilities,
    staleTime: 60 * 1000,
    placeholderData: DEFAULT_CAPABILITIES,
  });
  return (
    <Ctx.Provider value={data ?? DEFAULT_CAPABILITIES}>{children}</Ctx.Provider>
  );
}

export function useCapabilities(): Capabilities {
  return useContext(Ctx);
}
